import React from 'react';
import {Box, Typography} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * GeneratedReportContent - Renders the streaming markdown content for the generated report.
 */
const GeneratedReportContent = ({markdown, error}) => {
    return (
        <Box>
            {error && (
                <Typography variant="caption" color="error" sx={{display: 'block', mb: 1}}>
                    Failed to generate report: {String(error?.message || error)}
                </Typography>
            )}
            {!!markdown && (
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                        h1: ({node, ...props}) => (
                            <Typography variant="h4" fontWeight={600} sx={{mt: 3, mb: 1.5}} {...props} />
                        ),
                        h2: ({node, ...props}) => (
                            <Typography variant="h5" fontWeight={600} sx={{mt: 2.5, mb: 1.5}} {...props} />
                        ),
                        h3: ({node, ...props}) => (
                            <Typography variant="h6" fontWeight={600} sx={{mt: 2, mb: 1}} {...props} />
                        ),
                        h4: ({node, ...props}) => (
                            <Typography variant="subtitle1" fontWeight={600} sx={{mt: 2, mb: 1}} {...props} />
                        ),
                        h5: ({node, ...props}) => (
                            <Typography variant="subtitle2" fontWeight={600} sx={{mt: 1.5, mb: 0.5}} {...props} />
                        ),
                        h6: ({node, ...props}) => (
                            <Typography variant="subtitle2" fontWeight={600} sx={{mt: 1.5, mb: 0.5}} {...props} />
                        ),
                        p: ({node, ...props}) => (
                            <Typography variant="body1" sx={{mb: 1.5, lineHeight: 1.7}} {...props} />
                        ),
                        ul: ({node, ...props}) => (
                            <Box component="ul" sx={{pl: 3, mb: 1.5}} {...props} />
                        ),
                        ol: ({node, ...props}) => (
                            <Box component="ol" sx={{pl: 3, mb: 1.5}} {...props} />
                        ),
                        li: ({node, ...props}) => (
                            <Typography component="li" variant="body1" sx={{mb: 0.5}} {...props} />
                        ),
                        blockquote: ({node, ...props}) => (
                            <Box
                                component="blockquote"
                                sx={{
                                    borderLeft: '4px solid',
                                    borderColor: 'primary.main',
                                    pl: 2,
                                    py: 0.5,
                                    my: 2,
                                    fontStyle: 'italic',
                                    color: 'text.secondary'
                                }}
                                {...props}
                            />
                        ),
                        code: ({node, inline, className, children, ...props}) => {
                            if (inline) {
                                return (
                                    <Box
                                        component="code"
                                        sx={{
                                            backgroundColor: 'rgba(0,0,0,0.06)',
                                            px: 0.75,
                                            py: 0.25,
                                            borderRadius: '4px',
                                            fontSize: '0.9em',
                                            fontFamily: 'monospace'
                                        }}
                                        {...props}
                                    >
                                        {children}
                                    </Box>
                                );
                            }
                            return (
                                <Box
                                    component="pre"
                                    sx={{
                                        backgroundColor: '#f6f8fa',
                                        p: 2,
                                        borderRadius: 1,
                                        overflow: 'auto',
                                        my: 2
                                    }}
                                >
                                    <Box
                                        component="code"
                                        className={className}
                                        sx={{fontFamily: 'monospace'}}
                                        {...props}
                                    >
                                        {children}
                                    </Box>
                                </Box>
                            );
                        },
                        table: ({node, ...props}) => (
                            <Box sx={{overflowX: 'auto', my: 2}}>
                                <Box
                                    component="table"
                                    sx={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        '& th, & td': {
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            p: 1
                                        },
                                        '& th': {
                                            backgroundColor: 'grey.100',
                                            fontWeight: 600
                                        }
                                    }}
                                    {...props}
                                />
                            </Box>
                        )
                    }}
                >
                    {markdown}
                </ReactMarkdown>
            )}
        </Box>
    );
};

export default GeneratedReportContent;
